import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getSystemSettings, updateSystemSettings } from '../../api/settings';
import { updateProxyHost } from '../../api/proxy-hosts';
import type { ProxyHost } from '../../types/proxy-host';

interface ExceptionsTabProps {
  exploitEnabledHosts: ProxyHost[];
  isLoading: boolean;
  isError: boolean;
  onManageHost: (host: ProxyHost) => void;
}

export function ExceptionsTab({
  exploitEnabledHosts,
  isLoading,
  isError,
  onManageHost,
}: ExceptionsTabProps) {
  const { t } = useTranslation('exploitRules');
  const [exceptionTab, setExceptionTab] = useState<'global' | 'hosts'>('global');

  return (
    <div className="space-y-4">
      {/* Sub-tabs for exceptions */}
      <div className="flex gap-2">
        <button
          onClick={() => setExceptionTab('global')}
          className={`px-4 py-2 text-sm rounded-lg transition-colors ${exceptionTab === 'global'
            ? 'bg-amber-600 text-white'
            : 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-slate-600'
          }`}
        >
          {t('exceptions.globalTab')}
        </button>
        <button
          onClick={() => setExceptionTab('hosts')}
          className={`px-4 py-2 text-sm rounded-lg transition-colors ${exceptionTab === 'hosts'
            ? 'bg-amber-600 text-white'
            : 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-slate-600'
          }`}
        >
          {t('exceptions.hostTab')}
        </button>
      </div>

      {exceptionTab === 'global' ? (
        <GlobalExploitExceptions />
      ) : (
        <>
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
            </div>
          ) : isError ? (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-600 dark:text-red-400">
              {t('hostList.loadError')}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {exploitEnabledHosts.map((host) => (
                <ExceptionHostCard
                  key={host.id}
                  host={host}
                  onManage={() => onManageHost(host)}
                />
              ))}
              {exploitEnabledHosts.length === 0 && (
                <div className="col-span-full bg-gray-50 dark:bg-slate-800/50 rounded-lg p-8 text-center text-gray-500 dark:text-slate-400">
                  {t('hostList.noHosts')}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ExceptionHostCard({
  host,
  onManage,
}: {
  host: ProxyHost;
  onManage: () => void;
}) {
  const { t } = useTranslation('exploitRules');

  const exceptionCount = host.block_exploits_exceptions
    ? host.block_exploits_exceptions.split('\n').filter(line => line.trim() && !line.trim().startsWith('#')).length
    : 0;

  return (
    <div className="rounded-lg border p-4 bg-amber-50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-900/30">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white">
              {host.domain_names?.[0] || 'Unnamed Host'}
            </h3>
            <span className="px-2 py-0.5 text-xs rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">
              {t('hostList.status.exploitBlockEnabled')}
            </span>
          </div>
        </div>
      </div>

      <div className="mt-4 pt-3 border-t border-amber-200 dark:border-amber-900/30 flex items-center justify-between">
        <div className="text-sm">
          {exceptionCount > 0 ? (
            <span className="text-amber-600 dark:text-amber-400 font-medium">
              {t('exceptions.exceptionsCount', { count: exceptionCount })}
            </span>
          ) : (
            <span className="text-gray-500 dark:text-slate-500">{t('exceptions.noExceptions')}</span>
          )}
        </div>
        <button
          onClick={onManage}
          className="px-3 py-1.5 text-sm rounded-lg transition-colors bg-amber-600 text-white hover:bg-amber-700 dark:hover:bg-amber-500"
        >
          {t('exceptions.manage')}
        </button>
      </div>
    </div>
  );
}

function GlobalExploitExceptions() {
  const { t } = useTranslation('exploitRules');
  const queryClient = useQueryClient();
  const [exceptions, setExceptions] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  const settingsQuery = useQuery({
    queryKey: ['system-settings'],
    queryFn: getSystemSettings,
  });

  useEffect(() => {
    if (settingsQuery.data) {
      setExceptions(settingsQuery.data.global_block_exploits_exceptions || '');
    }
  }, [settingsQuery.data]);

  const updateMutation = useMutation({
    mutationFn: (newExceptions: string) =>
      updateSystemSettings({ global_block_exploits_exceptions: newExceptions }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system-settings'] });
      setHasChanges(false);
    },
  });

  const handleExceptionsChange = (value: string) => {
    setExceptions(value);
    setHasChanges(value !== (settingsQuery.data?.global_block_exploits_exceptions || ''));
  };

  const handleSave = () => {
    updateMutation.mutate(exceptions);
  };

  const handleReset = () => {
    setExceptions(settingsQuery.data?.global_block_exploits_exceptions || '');
    setHasChanges(false);
  };

  const exceptionCount = exceptions
    ? exceptions.split('\n').filter(line => line.trim() && !line.trim().startsWith('#')).length
    : 0;

  if (settingsQuery.isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border dark:border-slate-700 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b dark:border-slate-700 bg-gray-50 dark:bg-slate-900">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 flex items-center justify-center">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('exceptions.globalTitle')}</h2>
            <p className="text-sm text-gray-500 dark:text-slate-400">
              {t('exceptions.exceptionCount', { count: exceptionCount })}
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
            {t('exceptions.exceptionsLabel')}
          </label>
          <textarea
            value={exceptions}
            onChange={(e) => handleExceptionsChange(e.target.value)}
            placeholder={t('exceptions.placeholder')}
            className="w-full h-64 px-4 py-3 font-mono text-sm border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white dark:bg-slate-700 text-gray-900 dark:text-white dark:placeholder-slate-400"
          />
          <p className="mt-2 text-sm text-gray-500 dark:text-slate-400">
            {t('exceptions.description')}
          </p>
        </div>

        {/* Info Box */}
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-900/40 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="text-sm text-amber-800 dark:text-amber-200">
              <p className="font-medium">{t('exceptions.infoTitle')}</p>
              <p className="mt-1 text-amber-700 dark:text-amber-300">{t('exceptions.infoDescription')}</p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t dark:border-slate-700">
          {hasChanges && (
            <button
              onClick={handleReset}
              className="px-4 py-2 text-sm text-gray-700 dark:text-slate-300 bg-gray-100 dark:bg-slate-700 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors"
            >
              {t('common.reset')}
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={!hasChanges || updateMutation.isPending}
            className={`px-4 py-2 text-sm rounded-lg transition-colors ${
              hasChanges
                ? 'bg-amber-600 text-white hover:bg-amber-700 dark:hover:bg-amber-500'
                : 'bg-gray-200 dark:bg-slate-700 text-gray-400 dark:text-slate-500 cursor-not-allowed'
            }`}
          >
            {updateMutation.isPending ? t('common.saving') : t('common.save')}
          </button>
        </div>

        {updateMutation.isSuccess && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-900/40 rounded-lg p-3 text-sm text-green-700 dark:text-green-400">
            {t('exceptions.saveSuccess')}
          </div>
        )}

        {updateMutation.isError && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/40 rounded-lg p-3 text-sm text-red-700 dark:text-red-400">
            {t('exceptions.saveError')}
          </div>
        )}
      </div>
    </div>
  );
}

export function HostExceptionsModal({
  host,
  onClose,
}: {
  host: ProxyHost;
  onClose: () => void;
}) {
  const { t } = useTranslation('exploitRules');
  const queryClient = useQueryClient();
  const [exceptions, setExceptions] = useState(host.block_exploits_exceptions || '');
  const [hasChanges, setHasChanges] = useState(false);

  const updateMutation = useMutation({
    mutationFn: (newExceptions: string) =>
      updateProxyHost(host.id, { block_exploits_exceptions: newExceptions }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['proxy-hosts'] });
      setHasChanges(false);
    },
  });

  const handleExceptionsChange = (value: string) => {
    setExceptions(value);
    setHasChanges(value !== (host.block_exploits_exceptions || ''));
  };

  const handleSave = () => {
    updateMutation.mutate(exceptions);
  };

  const exceptionCount = exceptions
    ? exceptions.split('\n').filter(line => line.trim() && !line.trim().startsWith('#')).length
    : 0;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="absolute inset-4 md:inset-8 lg:inset-y-12 lg:inset-x-1/4 bg-white dark:bg-slate-800 rounded-xl shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex-shrink-0 px-6 py-4 border-b dark:border-slate-700 bg-gray-50 dark:bg-slate-900 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              {t('exceptions.hostModalTitle', { name: host.domain_names?.[0] || 'Unnamed' })}
            </h2>
            <p className="text-sm text-gray-500 dark:text-slate-400 mt-0.5">
              {t('exceptions.hostModalSubtitle', { count: exceptionCount })}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-200 dark:hover:bg-slate-700 rounded-lg transition-colors text-slate-500 dark:text-slate-400"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
              {t('exceptions.hostExceptionsLabel')}
            </label>
            <textarea
              value={exceptions}
              onChange={(e) => handleExceptionsChange(e.target.value)}
              placeholder={t('exceptions.hostPlaceholder')}
              className="w-full h-64 px-4 py-3 font-mono text-sm border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white dark:bg-slate-700 text-gray-900 dark:text-white dark:placeholder-slate-400"
            />
            <p className="mt-2 text-sm text-gray-500 dark:text-slate-400">
              {t('exceptions.hostDescription')}
            </p>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-900/40 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="text-sm text-blue-800 dark:text-blue-200">
                <p className="font-medium">{t('exceptions.hostInfoTitle')}</p>
                <p className="mt-1 text-blue-700 dark:text-blue-300">{t('exceptions.hostInfoDescription')}</p>
              </div>
            </div>
          </div>

          {updateMutation.isSuccess && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-900/40 rounded-lg p-3 text-sm text-green-700 dark:text-green-400">
              {t('exceptions.saveSuccess')}
            </div>
          )}

          {updateMutation.isError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/40 rounded-lg p-3 text-sm text-red-700 dark:text-red-400">
              {t('exceptions.saveError')}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 px-6 py-4 border-t dark:border-slate-700 bg-gray-50 dark:bg-slate-900 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 dark:text-slate-300 bg-gray-100 dark:bg-slate-700 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors"
          >
            {t('common.close')}
          </button>
          <button
            onClick={handleSave}
            disabled={!hasChanges || updateMutation.isPending}
            className={`px-4 py-2 text-sm rounded-lg transition-colors ${
              hasChanges
                ? 'bg-amber-600 text-white hover:bg-amber-700 dark:hover:bg-amber-500'
                : 'bg-gray-200 dark:bg-slate-700 text-gray-400 dark:text-slate-500 cursor-not-allowed'
            }`}
          >
            {updateMutation.isPending ? t('common.saving') : t('common.save')}
          </button>
        </div>
      </div>
    </div>
  );
}
